import { type ChangeEvent, memo, useCallback, useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { Input } from '@m/alchemy-ui/Input';
import { DeleteIcon } from '@m/alchemy-ui/icons/DeleteIcon';
import { VisuallyHidden } from '@m/alchemy-ui/VisuallyHidden';
import { SingleUpload } from '../../../../common/components';
import { type File as FileType } from '../../../../common/core-api';
import { FileUploadType } from '../../../../common/enums';
import { useFile, useImageUrl } from '../../../../hooks';
import { type PanelButtonConfig } from '../../common/button-link-config';
import { type PanelConfigPatch } from '../../common/panel-grid';
import { type PageCmsPanel } from '../../common/panel-types';
import {
  PanelEditorLabel,
  PanelEditorSection,
  PanelEditorStretchSection,
  PanelImageDeleteButton,
  PanelImagePreview,
  PanelImagePreviewWrapper,
  PanelInfoText
} from '../../CustomPage.styled';
import { LinkConfigSelect } from './LinkConfigSelect';

const ALT_TEXT_MAX_LENGTH = 300;

const messages = defineMessages({
  attachImage: {
    id: 'attachImage',
    defaultMessage: 'Attach image'
  },
  attachPanelImage: {
    id: 'editCustomPageAttachPanelImage',
    defaultMessage: 'Attach panel image'
  },
  panelImage: {
    id: 'editCustomPagePanelImage',
    defaultMessage: 'Panel image'
  },
  uploadInstructions: {
    id: 'editCustomPageBannerUploadInstructions',
    defaultMessage: 'Drag & Drop or manually attach an image'
  },
  deleteIcon: {
    id: 'deleteIcon',
    defaultMessage: 'Delete icon'
  },
  delete: {
    id: 'delete',
    defaultMessage: 'Delete'
  },
  widthPx: {
    id: 'widthPx',
    defaultMessage: 'Width (px)'
  },
  blankForFullWidth: {
    id: 'blankForFullWidth',
    defaultMessage: 'Blank for full-width'
  },
  alternativeText: {
    id: 'alternativeText',
    defaultMessage: 'Alternative text'
  },
  alternativeTextHelp: {
    id: 'editCustomPageAlternativeTextHelp',
    defaultMessage: 'Describe informative images. Leave blank only when the image is decorative.'
  },
  roundedCorners: {
    id: 'roundedCorners',
    defaultMessage: 'Rounded corners'
  }
});

type ImageConfig = Extract<PageCmsPanel['settings'], { __typename: 'ImagePanelSettings' }>;

interface ImagePanelEditorProps {
  readonly config: ImageConfig;
  readonly panelId: string;
  readonly showPanelErrors: boolean;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}

const ImagePanelEditorComponent = ({ config, panelId, showPanelErrors, onConfigChange }: ImagePanelEditorProps) => {
  const intl = useIntl();
  const id = useId();
  const [, uploadExtensions] = useFile();
  const widthInputId = `image-width-${id}`;
  const altTextInputId = `image-alt-text-${id}`;
  const altTextHelpId = `image-alt-text-help-${id}`;
  const uploadLabelId = `image-upload-label-${id}`;
  const uploadInstructionsId = `image-upload-instructions-${id}`;

  // Untransformed source; the preview is sized by the imageWidth setting below.
  const imageUrl = useImageUrl(config.imageFile ?? null, 0, 0);

  const handleFileUpload = useCallback(
    (file: FileType) => {
      onConfigChange({
        imageFile: { id: file.id, hash: file.hash, extension: file.extension, url: file.url, name: file.name }
      });
    },
    [onConfigChange]
  );

  const handleFileDelete = useCallback(() => onConfigChange({ imageFile: null }), [onConfigChange]);

  const handleWidthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onConfigChange({ imageWidth: event.target.value || null }),
    [onConfigChange]
  );

  const handleAltTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onConfigChange({ imageAltText: event.target.value || null }),
    [onConfigChange]
  );

  const handleRoundedCornersChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onConfigChange({ imageRoundedCorners: event.target.checked }),
    [onConfigChange]
  );

  const handleButtonConfigChange = useCallback(
    (buttonConfig: PanelButtonConfig) => onConfigChange({ buttonConfig }),
    [onConfigChange]
  );

  return (
    <>
      <PanelEditorStretchSection data-testid="image-panel-image-section">
        {config.imageFile ? (
          <PanelImagePreviewWrapper>
            <PanelImagePreview
              src={imageUrl}
              alt={config.imageAltText ?? ''}
              $width={config.imageWidth}
              $rounded={config.imageRoundedCorners}
              data-testid="image-panel-preview"
            />
            <PanelImageDeleteButton
              icon={<DeleteIcon aria-label={intl.formatMessage(messages.deleteIcon)} />}
              tooltipContents={intl.formatMessage(messages.delete)}
              onClick={handleFileDelete}
              data-testid="image-panel-delete-image-button"
            />
          </PanelImagePreviewWrapper>
        ) : (
          <SingleUpload
            buttonLabel={intl.formatMessage(messages.attachImage)}
            buttonAriaLabel={intl.formatMessage(messages.attachPanelImage)}
            fileTypes={uploadExtensions.image}
            type={FileUploadType.PAGE_HEADER}
            labelId={uploadLabelId}
            describedBy={uploadInstructionsId}
            currentFile={null}
            onFileUpload={handleFileUpload}
            onFileDelete={handleFileDelete}
          >
            <VisuallyHidden id={uploadLabelId}>{intl.formatMessage(messages.panelImage)}</VisuallyHidden>
            <p id={uploadInstructionsId}>{intl.formatMessage(messages.uploadInstructions)}</p>
          </SingleUpload>
        )}
      </PanelEditorStretchSection>
      <PanelEditorSection>
        <PanelEditorLabel as="label" htmlFor={widthInputId}>
          {intl.formatMessage(messages.widthPx)}
        </PanelEditorLabel>
        <Input
          id={widthInputId}
          isFullWidth
          value={config.imageWidth ?? ''}
          onChange={handleWidthChange}
          data-testid="image-panel-width-input"
        />
        <PanelInfoText>{intl.formatMessage(messages.blankForFullWidth)}</PanelInfoText>
      </PanelEditorSection>
      <PanelEditorSection>
        <PanelEditorLabel as="label" htmlFor={altTextInputId}>
          {intl.formatMessage(messages.alternativeText)}
        </PanelEditorLabel>
        <Input
          id={altTextInputId}
          isFullWidth
          maxLength={ALT_TEXT_MAX_LENGTH}
          value={config.imageAltText ?? ''}
          onChange={handleAltTextChange}
          aria-describedby={altTextHelpId}
          data-testid="image-panel-alt-text-input"
        />
        <PanelInfoText id={altTextHelpId}>{intl.formatMessage(messages.alternativeTextHelp)}</PanelInfoText>
      </PanelEditorSection>
      <PanelEditorSection>
        <Checkbox
          label={intl.formatMessage(messages.roundedCorners)}
          checked={config.imageRoundedCorners}
          onChange={handleRoundedCornersChange}
          data-testid="image-panel-rounded-corners-checkbox"
        />
      </PanelEditorSection>
      <LinkConfigSelect
        panelId={panelId}
        variant="link"
        buttonConfig={config.buttonConfig ?? null}
        showPanelErrors={showPanelErrors}
        onChange={handleButtonConfigChange}
      />
    </>
  );
};

// Immutable config updates make prop comparison cheap: the editor re-renders only when its
// own config (or the grid callback) changes.
export const ImagePanelEditor = memo(ImagePanelEditorComponent);
